import React, { useState, useEffect, useRef } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import { type Instance as TippyInstance } from 'tippy.js/index';
import Tool, { type IToolEnabledInput, type ToolButtonComponent } from './Tool';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolButton from '../toolbars/ToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import { isDevice } from '../objects/guards';
import { DEVICE_SELECTION_ERROR_MESSAGE } from '../utils/tools';

const Popup: React.FC<{
  currentDeviceName: string;
  setCurrentDeviceName: (shape: string) => void;
  selection: fabric.Object;
  hidePopup: () => void;
}> = ApplicationView(
  ({ currentDeviceName, setCurrentDeviceName, selection, hidePopup }) => {
    if (!isDevice(selection)) {
      console.error(DEVICE_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const controller = useController(WhiteboardController);

    const setDeviceName = deviceName => {
      hidePopup();
      setCurrentDeviceName(deviceName);
      selection.setOnObjects({
        deviceName,
      });

      controller.actionForceRerenderObjectToolbar();
      Tool.toolActionComplete({ controller });
    };

    return (
      <ToolbarMenu>
        {[...controller.devices].map((deviceName, i) => (
          <ToolButton
            active={deviceName === currentDeviceName}
            key={i}
            onClick={() => setDeviceName(deviceName)}
          >
            {selection.renderStandardIcon(deviceName)}
          </ToolButton>
        ))}
      </ToolbarMenu>
    );
  }
);

const WireframeDeviceTypeButton: ToolButtonComponent = ApplicationView(
  ({ selection, tool }) => {
    if (!isDevice(selection)) {
      console.error(DEVICE_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const deviceName = selection.deviceName;
    const [currentDeviceName, setCurrentDeviceName] = useState(deviceName);
    const tippyRef = useRef<TippyInstance>();

    // Update color if the props change.
    useEffect(() => {
      setCurrentDeviceName(deviceName);
    }, [deviceName]);

    return (
      <ToolButton
        popup={
          <Popup
            currentDeviceName={currentDeviceName}
            setCurrentDeviceName={setCurrentDeviceName}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        tippyRef={tippyRef}
        tool={tool}
      >
        {selection.renderStandardIcon(currentDeviceName)}
      </ToolButton>
    );
  }
);

class ToolWireframeDeviceType extends Tool {
  static toolName = 'Device';

  static available({ selection }: IToolEnabledInput): boolean {
    return selection?.type === 'device';
  }

  static buttonComponent() {
    return WireframeDeviceTypeButton;
  }
}

export default ToolWireframeDeviceType;
